import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, Typography, IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Switch, Tooltip } from '@mui/material';
import { Add, Delete, Edit, Save } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { updateTenant } from '../store/authSlice';
import api from '../services/api';
import ArcaSetupWizard from '../components/invoicing/ArcaSetupWizard';
import FiscalSummaryCard from '../components/invoicing/FiscalSummaryCard';
import TextField from '../components/common/TextField';

export const SettingsPage: React.FC = () => {
  const { tenant } = useSelector((s: RootState) => s.auth);
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState(tenant?.name || '');
  const [primaryColor, setPrimaryColor] = useState(tenant?.primaryColor || '');
  const [secondaryColor, setSecondaryColor] = useState(tenant?.secondaryColor || '');
  // Categories
  const [categories, setCategories] = useState<{ id: number; name: string; isActive: boolean; sortOrder: number }[]>([]);
  const [catName, setCatName] = useState('');
  const [saving, setSaving] = useState(false);
  // Fixed expenses
  const [expenses, setExpenses] = useState<{ id: number; name: string; amountMonthly: number; isActive: boolean }[]>([]);
  const [fxName, setFxName] = useState('');
  const [fxAmount, setFxAmount] = useState<number>(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getCategories();
        setCategories((res.items || []).sort((a,b)=> (a.sortOrder-b.sortOrder)||a.name.localeCompare(b.name)));
      } catch {}
      try {
        const fx = await api.getFixedExpenses();
        setExpenses(fx as any);
      } catch {}
    })();
  }, []);

  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [fiscalVersion, setFiscalVersion] = useState(0);
  const saveServer = async () => {
    setSaveMessage(null);
    try {
      const updated = await api.updateTenantMe({ name, primaryColor, secondaryColor });
      dispatch(updateTenant({
        name: updated.name,
        primaryColor: updated.primaryColor,
        secondaryColor: updated.secondaryColor,
        logoUrl: updated.logoUrl,
      } as any));
      setSaveMessage('Guardado en servidor');
    } catch (e: any) {
      setSaveMessage(e?.response?.data?.message || 'Error al guardar');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>Configuración</Typography>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Datos del tenant</Typography>
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <TextField label="Nombre" value={name} onChange={e => setName(e.target.value)} />
            <TextField label="Color primario" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} placeholder="#384df4" />
            <TextField label="Color secundario" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} placeholder="#F6D058" />
            <Button variant="contained" onClick={saveServer}>Guardar</Button>
            {saveMessage && (
              <Typography variant="caption" color={saveMessage.includes('Error') ? 'error' : 'success.main'}>{saveMessage}</Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6">Categorías</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Nueva categoría" value={catName} onChange={e => setCatName(e.target.value)} />
              <Button startIcon={<Add />} variant="contained" disabled={!catName.trim() || saving} onClick={async () => { try { setSaving(true); const created = await api.createCategory({ name: catName.trim(), isActive: true }); setCategories(prev => [...prev, created].sort((a,b)=> (a.sortOrder-b.sortOrder)||a.name.localeCompare(b.name))); setCatName(''); } finally { setSaving(false); } }}>Agregar</Button>
            </Stack>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="center">Activa</TableCell>
                  <TableCell align="right">Orden</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((c, idx) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <TextField size="small" value={c.name} onChange={e => { const name = e.target.value; setCategories(prev => prev.map(x => x.id === c.id ? { ...x, name } : x)); }} />
                    </TableCell>
                    <TableCell align="center">
                      <Switch checked={c.isActive} onChange={(_, v) => setCategories(prev => prev.map(x => x.id === c.id ? { ...x, isActive: v } : x))} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={c.sortOrder} onChange={e => { const v = Number(e.target.value); setCategories(prev => prev.map(x => x.id === c.id ? { ...x, sortOrder: v } : x)); }} sx={{ width: 100 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Guardar"><span><IconButton disabled={saving} onClick={async ()=>{ try { setSaving(true); const upd = await api.updateCategory(c.id, { name: c.name, isActive: c.isActive, sortOrder: c.sortOrder }); setCategories(prev => prev.map(x => x.id === c.id ? upd : x).sort((a,b)=> (a.sortOrder-b.sortOrder)||a.name.localeCompare(b.name))); } finally { setSaving(false); } }}><Save /></IconButton></span></Tooltip>
                        <Tooltip title="Eliminar"><span><IconButton disabled={saving} onClick={async ()=>{ if (!window.confirm('¿Eliminar categoría?')) return; try { setSaving(true); await api.deleteCategory(c.id); setCategories(prev => prev.filter(x => x.id !== c.id)); } finally { setSaving(false); } }}><Delete /></IconButton></span></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {categories.length === 0 && (
                  <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No hay categorías configuradas.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="h6">Gastos fijos (mensuales)</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" label="Nombre" value={fxName} onChange={e => setFxName(e.target.value)} />
              <TextField size="small" type="number" label="Monto mensual" value={fxAmount} onChange={e => setFxAmount(Number(e.target.value || 0))} />
              <Button startIcon={<Add />} variant="contained" disabled={!fxName.trim() || fxAmount <= 0 || saving} onClick={async () => { try { setSaving(true); const created = await api.createFixedExpense({ name: fxName.trim(), amountMonthly: fxAmount }); setExpenses(prev => [created, ...prev]); setFxName(''); setFxAmount(0); } finally { setSaving(false); } }}>Agregar</Button>
            </Stack>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell align="right">Monto mensual</TableCell>
                  <TableCell align="center">Activo</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>
                      <TextField size="small" value={e.name} onChange={ev => { const name = ev.target.value; setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, name } : x)); }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" type="number" value={e.amountMonthly} onChange={ev => { const v = Number(ev.target.value || 0); setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, amountMonthly: v } : x)); }} sx={{ width: 160 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Switch checked={e.isActive} onChange={(_, v) => setExpenses(prev => prev.map(x => x.id === e.id ? { ...x, isActive: v } : x))} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Guardar"><span><IconButton disabled={saving} onClick={async ()=>{ try { setSaving(true); const upd = await api.updateFixedExpense(e.id, { name: e.name, amountMonthly: e.amountMonthly, isActive: e.isActive }); setExpenses(prev => prev.map(x => x.id === e.id ? upd : x)); } finally { setSaving(false); } }}><Save /></IconButton></span></Tooltip>
                        <Tooltip title="Eliminar"><span><IconButton disabled={saving} onClick={async ()=>{ if (!window.confirm('¿Eliminar gasto fijo?')) return; try { setSaving(true); await api.deleteFixedExpense(e.id); setExpenses(prev => prev.filter(x => x.id !== e.id)); } finally { setSaving(false); } }}><Delete /></IconButton></span></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {expenses.length === 0 && (
                  <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">No hay gastos fijos configurados.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ArcaSetupWizard onSaved={() => setFiscalVersion(v => v + 1)} />
      <FiscalSummaryCard key={fiscalVersion} />
    </Box>
  );
};
